import { FinanceType, AncillaryProductLenderStanceType } from "@cf247/pricing/dist/enums";
import type { MonthlyPrice, Vehicle, Dealer } from "@cf247/pricing/dist/types";
import { toUkIsoString } from "@/lib/utils";
import type {
  QuoteDealerDetail,
  QuoteRequestModel,
  QuoteDetailModel,
  LoanProposalRequest,
  QuoteVehicleExtra,
} from "@/models/underwriting";

/** QuoteStatus.Init */
const QUOTE_STATUS_INIT = 0;
/** VehicleTypes.Car */
const VEHICLE_TYPE_CAR = 1;
/** DealerStateType.ApprovedDCIOnly — the only setup status a pricing snapshot dealer can convey (via `dciOnly`). */
const DEALER_STATE_APPROVED_DCI_ONLY = 7;
/** VehicleExtraPaymentMethod.IncludeInFinance */
const EXTRA_PAYMENT_INCLUDE_IN_FINANCE = 0;
/** VehicleExtraPaymentMethod.Cash */
const EXTRA_PAYMENT_CASH = 1;

function parseScore(rating: string | undefined): number | null {
  if (rating == null || rating === "") return null;
  const n = Number(rating);
  return Number.isFinite(n) ? n : null;
}

/**
 * The persisted snapshot dealer is the workscreens `mapDealer` output, which spreads
 * a phoenix DealerResponseModel over the pricing `Dealer` shape. Most values therefore
 * live under their phoenix names (`dealerName`, `url`, `avgScore`, `dealerSetupStatusId`,
 * `dealerTypeId`) rather than the pricing names (`name`, `websiteUrl`, `rating`).
 */
type SnapshotDealer = Dealer & {
  dealerName?: string;
  url?: string;
  avgScore?: number | null;
  dealerSetupStatusId?: number | null;
  dealerTypeId?: number | null;
};

/** Maps a pricing snapshot dealer onto the phoenix DealerDetailModel shape. */
export function mapDealerFromSnapshot(dealer: Dealer | undefined): QuoteDealerDetail | null {
  if (!dealer) return null;
  // Read both the pricing names and the phoenix names the snapshot actually persists.
  const d = dealer as SnapshotDealer;
  return {
    id: d.id ?? null,
    name: d.name ?? d.dealerName ?? "",
    phoneNumber: d.phone ?? "",
    webSiteUrl: d.websiteUrl ?? d.url ?? "",
    avgScore: d.avgScore ?? parseScore(d.rating),
    town: d.town ?? "",
    postcode: d.postcode ?? "",
    adminFee: d.adminFee?.amount ?? 0,
    // Prefer the persisted setup status; fall back to the pricing `dciOnly` flag.
    state: d.dealerSetupStatusId ?? (d.dciOnly ? DEALER_STATE_APPROVED_DCI_ONLY : null),
    type: d.type ?? d.dealerTypeId ?? null,
  };
}

/**
 * Builds the quote's vehicle extras from a pricing result. Only the extras the
 * lender actually included (`extrasIncluded`) are sent; their name/price come from
 * the matching `extrasPrices` entry, and the finance stance maps to a payment method.
 *
 * The extra's type id/name aren't present on the pricing result, so they're taken
 * from the snapshot's `includedExtras` (persisted at request time) when available.
 */
export function mapVehicleExtrasFromResult(
  pricingResult: MonthlyPrice,
  snapshotExtras?:
    | {
        productId: number;
        vehicleExtraTypeId?: number;
        vehicleExtraTypeName?: string;
        soldByAgentId?: number | null;
      }[]
    | null
): QuoteVehicleExtra[] {
  const included = pricingResult.extrasIncluded ?? [];
  if (!included.length) return [];
  const priceLookup = new Map(
    (pricingResult.extrasPrices ?? []).map((extra) => [extra.productId, extra])
  );
  const typeLookup = new Map(
    (snapshotExtras ?? []).map((extra) => [extra.productId, extra])
  );
  return included.map((inc) => {
    const info = priceLookup.get(inc.productId);
    const type = typeLookup.get(inc.productId);
    const inFinance = inc.stance === AncillaryProductLenderStanceType.Finance;
    return {
      vehicleExtraOptionId: inc.productId,
      // Type id/name aren't on the pricing result; use the snapshot's when present.
      vehicleExtraTypeId: type?.vehicleExtraTypeId ?? 0,
      vehicleExtraTypeName: type?.vehicleExtraTypeName ?? "",
      description: info?.productName ?? "",
      price: info?.productPrice ?? 0,
      paymentMethod: inFinance ? EXTRA_PAYMENT_INCLUDE_IN_FINANCE : EXTRA_PAYMENT_CASH,
      isCash: !inFinance,
      // The selling agent isn't on the pricing result; use the snapshot's when present.
      soldByAgentId: type?.soldByAgentId ?? null,
      warrantyStartDate: null,
      isEmptyPlaceholder: false,
      isDisabled: false,
      isDeleted: false,
      minPrice: null,
      maxPrice: null,
    };
  });
}


/**
 * Builds the create-quote payload from a pricing result and the quote snapshot
 * data. Mirrors `mapMonthlyPriceToQuoteRequest` in the workscreens plugin.
 */
export function mapMonthlyPriceToQuoteRequest(
  pricingResult: MonthlyPrice,
  applicationId: number,
  vehicle: Vehicle | undefined,
  dealer: Dealer | undefined,
  createdByUserId: number | null,
  snapshotExtras?:
    | {
        productId: number;
        vehicleExtraTypeId?: number;
        vehicleExtraTypeName?: string;
        soldByAgentId?: number | null;
      }[]
    | null
): QuoteRequestModel {
  return {
    applicationId,
    lenderId: pricingResult.lenderId,
    lenderName: pricingResult.lenderName,
    tierId: pricingResult.proposal?.tierId ?? null,
    deposit: pricingResult.deposit,
    term: pricingResult.term,
    amountToFinance: pricingResult.balanceToFinance,
    monthlyPayment: pricingResult.monthlyPayment,
    firstMonthlyPayment: pricingResult.firstMonthlyPayment ?? null,
    finalPayment: pricingResult.finalMonthlyPayment ?? null,
    totalAmountPayable: pricingResult.totalAmountPayable,
    costOfCredit: pricingResult.costOfCredit ?? null,
    apr: pricingResult.effectiveRate ?? 0,
    vehiclePrice: pricingResult.vehiclePrice,
    isPCPPricing: pricingResult.financeType === FinanceType.PCP,
    annualMileage: pricingResult.annualMileage ?? undefined,
    partExchangeValue: pricingResult.pxValue ?? null,
    partExchangeSettlement: pricingResult.pxSettlement ?? null,
    lendersCharges: pricingResult.loanFees ?? null,
    lendersPostFees: pricingResult.postFees ?? null,
    commissionAmount: pricingResult.commissionAmount ?? undefined,
    affiliateCommissionAmount: pricingResult.affiliateCommissionAmount ?? undefined,
    status: QUOTE_STATUS_INIT,
    quoteId: 0,
    lenderReference: "",
    vehicleId: vehicle?.vehicleId ?? null,
    vehicleRegistration: vehicle?.vrm ?? "",
    vehicleMake: vehicle?.make ?? "",
    vehicleModel: vehicle?.model ?? "",
    vehicleMileage: vehicle?.mileage ?? null,
    vehicleColour: vehicle?.colour ?? "",
    vehicleImageUrl: vehicle?.images?.[0] ?? "",
    vehicleRegistrationDate: vehicle?.registrationDate
      ? toUkIsoString(new Date(vehicle.registrationDate))
      : null,
    vehicleType: (vehicle?.vehicleType as number | undefined) ?? VEHICLE_TYPE_CAR,
    loanVehicleId:
      (vehicle as (Vehicle & { loanVehicleId?: number }) | undefined)?.loanVehicleId ?? null,
    createdByUserId,
    isAccepted: false,
    acceptedQuoteId: null,
    statusReasonId: null,
    isArchived: false,
    dealer: mapDealerFromSnapshot(dealer),
    dealerFee: {
      amount: dealer?.adminFee?.amount ?? 0,
      includedInFinance: dealer?.adminFee?.includedInFinance ?? false,
    },
    vehicleExtras: mapVehicleExtrasFromResult(pricingResult, snapshotExtras),
    createdDate: toUkIsoString(),
  };
}

/**
 * Builds the loan-proposal payload from a saved quote. Mirrors `sendToLender`
 * in the workscreens plugin — rules are ignored because the proposal has
 * already been reviewed by underwriting.
 */
export function buildLoanProposalRequest(quote: QuoteDetailModel): LoanProposalRequest {
  return {
    applicationId: quote.applicationId,
    financeCompanyId: quote.lenderId,
    financeCompanyTierId: quote.tierId ?? undefined,
    secretQuestion: null,
    secretAnswer: null,
    ignoreRules: true,
    affiliateUserType: null,
  };
}
